/** The memory frame's state (spec §7): the globe, the focus and the trail behind the breadcrumb, the pocket's levels and the
 * user-wide suggestions to review. One globe request on open and refresh, one focus request per selection (§7.4). */
import type { StateCreator } from "zustand";
import type { ApiError } from "@/api/client";
import {
  decideMemoryReview,
  forgetThing,
  listMemoryReview,
  type MemoryReviewItem,
} from "@/api/memory";
import {
  YOU,
  editFact,
  getFocus,
  getGlobe,
  removeFact,
  type FactPatch,
  type FocusView,
  type GlobeView,
  type MemoryFact,
  type ThingKind,
} from "@/api/memory-focus";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { thingDisplayName } from "@/lib/memory-thing-kinds";
import {
  FOCUS_BOUND,
  POCKET_ROOT,
  followFocus,
  nextTrail,
  pocketState,
  type FocusMove,
  type MemorySelection,
  type PocketEntry,
  type PocketLevel,
} from "@/stores/memory-pocket-stack";
import type { MemoryState } from "@/stores/memory-store";
import { useProjectStore } from "@/stores/project-store";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";
/** What "N need your review" walks (spec §7.3). Procedures stay in the flat Settings proposals list (§7.6). */
const WALKED_TYPES: ReadonlySet<string> = new Set([
  "fact",
  "entity",
  "profile",
]);
export interface MemoryFrameSlice {
  globe: GlobeView | null;
  globeError: string | null;
  /** YOU (the root) or a thing id; `trail` ends with it. `thingNames` feed the breadcrumb; `youId` maps You's id to YOU. */
  focusId: string;
  trail: string[];
  thingNames: Record<string, string>;
  youId: string | null;
  /** The focus view read for `focusViewId`. */
  focusView: FocusView | null;
  focusViewId: string | null;
  focusError: string | null;
  /** The pocket's levels, root first; empty while the pocket is closed. */
  pocketStack: PocketEntry[];
  selection: MemorySelection;
  pocketLevel: PocketLevel | null;
  /** Pending fact, thing and profile suggestions, user-wide. `fitCount` bumps on Fit so the globe turns home even at You. */
  reviewItems: MemoryReviewItem[];
  fitCount: number;
  /** Bumps on every open so the globe turns to the thing even when it already is the focus. */
  aimCount: number;
  setFocus: (id: string, move?: FocusMove, name?: string) => void;
  /** Focuses a thing and shows it in the pocket (a globe click, search, Settings, agents). */
  openThing: (id: string, name?: string, move?: FocusMove) => void;
  select: (selection: NonNullable<MemorySelection>) => void;
  openLevel: (entry: PocketEntry) => void;
  /** The pocket's back chevron: one level up. */
  popPocket: () => void;
  /** Esc (mockup keydown): a deeper pocket level, else one step along the trail, else close the pocket. */
  back: () => void;
  closePocket: () => void;
  openSearch: () => void;
  fit: () => void;
  /** Opens a suggestion, or the first one, with its subject in focus. */
  openReview: (candidateId?: string) => void;
  stepReview: (delta: 1 | -1) => void;
  /** Rejects when the decision fails, after refreshing. */
  decideSuggestion: (
    candidateId: string,
    decision: "accept" | "reject",
  ) => Promise<void>;
  saveFact: (factId: string, patch: FactPatch) => Promise<MemoryFact>;
  removeFact: (factId: string) => Promise<void>;
  /** Deletes a thing with its facts; a focus on it or through it returns to You, and an open frame reloads. */
  forgetThing: (thingId: string) => Promise<void>;
  /** The loads never reject: failures set an error, a missing focus falls back to You, a failed review load keeps the list. */
  loadGlobe: () => Promise<void>;
  loadFocus: () => Promise<void>;
  loadReview: () => Promise<void>;
  refresh: () => Promise<void>;
}
type FrameData = Pick<
  MemoryFrameSlice,
  | "globe"
  | "globeError"
  | "focusId"
  | "trail"
  | "thingNames"
  | "youId"
  | "focusView"
  | "focusViewId"
  | "focusError"
  | "pocketStack"
  | "selection"
  | "pocketLevel"
  | "reviewItems"
  | "fitCount"
  | "aimCount"
>;
export const initialFrameState = (): FrameData => ({
  globe: null,
  globeError: null,
  focusId: YOU,
  trail: [YOU],
  thingNames: { [YOU]: "You" },
  youId: null,
  focusView: null,
  focusViewId: null,
  focusError: null,
  ...pocketState([]),
  reviewItems: [],
  fitCount: 0,
  aimCount: 0,
});
/** Where the globe looks while a suggestion is reviewed (mockup `openSuggestion`). */
export function reviewSubject(item: MemoryReviewItem): string | null {
  if (item.item_type === "profile") return YOU;
  return item.item_type === "fact"
    ? (item.source_node_id ?? item.target_node_id)
    : null;
}
const failure = (error: unknown) =>
  error instanceof Error
    ? error.message
    : translate(storesNoticesMessages, "memoryLoadFailed");
const currentProjectId = () => useProjectStore.getState().currentProject?.id;
export const createMemoryFrameSlice: StateCreator<
  MemoryState,
  [],
  [],
  MemoryFrameSlice
> = (set, get) => {
  let [globeRequest, focusRequest, focusInFlight] = [
    0,
    0,
    null as string | null,
  ];
  const canonical = (id: string) => (id === get().youId ? YOU : id);
  const topEntry = () => get().pocketStack[get().pocketStack.length - 1];
  const needsFocusView = () =>
    get().focusId !== YOU || FOCUS_BOUND.has(topEntry()?.level ?? "search");
  const syncFocus = () => {
    const { focusId, focusViewId } = get();
    if (
      needsFocusView() &&
      focusViewId !== focusId &&
      focusInFlight !== focusId
    )
      void get().loadFocus();
  };
  const namesOf = (
    things: Array<{ id: string | null; name: string; kind: ThingKind }>,
  ) =>
    Object.fromEntries(
      things
        .filter((thing) => thing.id)
        .map((thing) => [canonical(thing.id!), thingDisplayName(thing)]),
    );
  return {
    ...initialFrameState(),
    setFocus: (rawId, move = "push", name) => {
      const id = canonical(rawId);
      const state = get();
      if (name) set({ thingNames: { ...state.thingNames, [id]: name } });
      if (id !== state.focusId)
        set({
          focusId: id,
          trail: nextTrail(state.trail, id, move),
          ...pocketState(followFocus(state.pocketStack, id)),
        });
      syncFocus();
    },
    openThing: (rawId, name, move = "jump") => {
      get().setFocus(rawId, move, name);
      set((state) => ({
        aimCount: state.aimCount + 1,
        ...pocketState([POCKET_ROOT, { level: "thing", id: state.focusId }]),
      }));
      syncFocus();
    },
    select: (selection) => {
      if (selection.type === "thing")
        return get().openThing(selection.id, undefined, "push");
      if (selection.type === "suggestion")
        return get().openReview(selection.id);
      const { focusId } = get();
      set(
        pocketState(
          focusId === YOU && selection.type === "fact"
            ? [POCKET_ROOT, { level: "fact", id: selection.id }]
            : [
                POCKET_ROOT,
                { level: "thing", id: focusId },
                { level: selection.type, id: selection.id },
              ],
        ),
      );
      syncFocus();
    },
    openLevel: (entry) =>
      set((state) => pocketState([...state.pocketStack, entry])),
    popPocket: () => {
      set((state) => pocketState(state.pocketStack.slice(0, -1)));
      syncFocus();
    },
    back: () => {
      const { pocketStack, trail, focusId } = get();
      const top = pocketStack[pocketStack.length - 1];
      if (top && !(top.level === "thing" && top.id === focusId))
        return get().popPocket();
      const previous = trail[trail.length - 2];
      if (previous !== undefined) return get().setFocus(previous);
      if (top) get().closePocket();
    },
    closePocket: () => set(pocketState([])),
    openSearch: () => set(pocketState([POCKET_ROOT])),
    fit: () => {
      if (FOCUS_BOUND.has(topEntry()?.level ?? "search")) set(pocketState([]));
      set((state) => ({ fitCount: state.fitCount + 1 }));
      get().setFocus(YOU, "jump");
    },
    openReview: (candidateId) => {
      const items = get().reviewItems;
      const item =
        candidateId === undefined
          ? items[0]
          : items.find(({ id }) => id === candidateId);
      if (!item) return;
      const subject = reviewSubject(item);
      if (subject) get().setFocus(subject, "jump");
      set(pocketState([POCKET_ROOT, { level: "suggestion", id: item.id }]));
    },
    stepReview: (delta) => {
      const [current, items] = [topEntry(), get().reviewItems];
      if (current?.level !== "suggestion" || items.length === 0) return;
      const next =
        items[
          (items.findIndex(({ id }) => id === current.id) +
            delta +
            items.length) %
            items.length
        ];
      if (next) get().openReview(next.id);
    },
    decideSuggestion: async (candidateId, decision) => {
      const index = get().reviewItems.findIndex(({ id }) => id === candidateId);
      try {
        await decideMemoryReview(candidateId, decision);
      } finally {
        await get().refresh();
      }
      const current = topEntry();
      if (current?.level !== "suggestion" || current.id !== candidateId) return;
      const remaining = get().reviewItems;
      const next =
        remaining[Math.min(Math.max(index, 0), remaining.length - 1)];
      if (next) get().openReview(next.id);
      else get().closePocket();
    },
    saveFact: async (factId, patch) => {
      const saved = await editFact(factId, patch);
      await get().refresh();
      set((state) =>
        pocketState(
          state.pocketStack.map((entry) =>
            entry.level !== "thing" && entry.id === factId
              ? { ...entry, id: saved.id }
              : entry,
          ),
        ),
      );
      return saved;
    },
    removeFact: async (factId) => {
      await removeFact(factId);
      set((state) => {
        const stack = state.pocketStack;
        const atFact =
          stack[stack.length - 1]?.level === "fact" &&
          stack[stack.length - 1]?.id === factId;
        return {
          globe: null,
          focusView: null,
          focusViewId: null,
          ...(atFact ? pocketState(stack.slice(0, -1)) : {}),
        };
      });
      await get().refresh();
    },
    forgetThing: async (thingId) => {
      await forgetThing(thingId);
      const { focusId, trail, globe } = get();
      set({ globe: null, focusView: null, focusViewId: null });
      if (focusId === thingId || trail.includes(thingId)) {
        set(pocketState([]));
        get().setFocus(YOU, "jump");
      }
      if (globe) await get().refresh();
    },
    loadGlobe: async () => {
      const [isCurrentAuthScope, request] = [
        captureDesktopChatAuthScopeGuard(),
        ++globeRequest,
      ];
      try {
        const globe = await getGlobe(currentProjectId());
        if (request !== globeRequest || !isCurrentAuthScope()) return;
        set({
          globe,
          globeError: null,
          youId:
            globe.things.find(({ thing }) => thing.kind === "user_profile")
              ?.thing.id ?? get().youId,
        });
        set((state) => ({
          thingNames: {
            ...state.thingNames,
            ...namesOf(globe.things.map(({ thing }) => thing)),
          },
        }));
      } catch (error) {
        if (request === globeRequest && isCurrentAuthScope())
          set({ globeError: failure(error) });
      }
    },
    loadFocus: async () => {
      const [isCurrentAuthScope, request, focusId] = [
        captureDesktopChatAuthScopeGuard(),
        ++focusRequest,
        get().focusId,
      ];
      focusInFlight = focusId;
      set({ focusError: null });
      try {
        const view = await getFocus(focusId, currentProjectId());
        if (request !== focusRequest || !isCurrentAuthScope()) return;
        const things = [
          ...(view.focus ? [view.focus] : []),
          ...view.ring.map(({ thing }) => thing),
        ];
        set((state) => ({
          focusView: view,
          focusViewId: focusId,
          thingNames: { ...state.thingNames, ...namesOf(things) },
        }));
      } catch (error) {
        if (request !== focusRequest || !isCurrentAuthScope()) return;
        if (
          (error as Partial<ApiError> | null)?.status === 404 &&
          focusId !== YOU
        )
          return get().setFocus(YOU, "jump");
        set({ focusError: failure(error) });
      } finally {
        if (request === focusRequest) focusInFlight = null;
      }
    },
    loadReview: async () => {
      const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
      try {
        const reviewPage = await listMemoryReview({ state: "pending" });
        if (isCurrentAuthScope())
          set({
            reviewItems: reviewPage.items.filter(
              (item) =>
                item.status === "pending" && WALKED_TYPES.has(item.item_type),
            ),
          });
      } catch {
        // Keep the previous list; the next refresh retries.
      }
    },
    refresh: async () => {
      await Promise.all([
        get().loadGlobe(),
        get().loadReview(),
        needsFocusView() ? get().loadFocus() : Promise.resolve(),
      ]);
    },
  };
};
